import React from 'react';
import { useTranslation } from 'react-i18next';
import { Workspace } from '../NavWorkspaces';
import { accountsStorageKey, appTokenStorageKey } from 'App/components/utils/providers/AppUserProvider';
import { parseTokenResponse, TokenResponse } from 'Services/api/auth/base';
import { IAppUserMeta } from 'App/types';

export interface IWorkspaceHook {
  getWorkspaceName( workspace: Workspace ): string;
  isMultiTenantList( workspaces?: Workspace[] ): boolean;
  sortByTenantName( workspaces?: Workspace[] ): Workspace[];
  updateAccessToken( accessToken?: string ): void;
  updateAppAaccount( data?: TokenResponse ): void;
}

export const useWorkspaceHook = (): IWorkspaceHook => {
  const { t } = useTranslation( [ 'base' ] );

  const updateAppAaccount = React.useCallback( ( data?: TokenResponse ): void => {
    if ( data ) {
      const auth = parseTokenResponse( data );
      const { sub, tenant_id, tenant_slug, tenant_name } = auth.userInfo;
      const appAaccounts = JSON.parse(
        localStorage.getItem( accountsStorageKey ) as string,
      );
      const account = ( appAaccounts as IAppUserMeta[] )?.find( ( p ) => p.sub === sub );
      if ( account ) {
        if ( tenant_id ) {
          account.tenantId = tenant_id;
        }
        if ( tenant_slug ) {
          account.tenantSlug = tenant_slug;
          account.tenantName = tenant_name || tenant_slug;
        }

        localStorage.setItem( accountsStorageKey, JSON.stringify( appAaccounts ) );
      }
    }
  }, [] );

  const updateAccessToken = React.useCallback(
    ( accessToken?: string ): void => {
      if ( accessToken ) {
        const appTokens = JSON.parse(
          localStorage.getItem( appTokenStorageKey ) as string,
        );
        localStorage.setItem( appTokenStorageKey, JSON.stringify( {
          ...appTokens, accessToken,
        } ) );
      }
    }, [] );

  const sortByTenantName = React.useCallback(
    ( workspaces?: Workspace[] ): Workspace[] => {
      if ( workspaces ) {
        return workspaces.sort( ( a, b ) => a.name.localeCompare( b.tenantName ) );
      }

      return [];
    },
    [],
  );

  const isMultiTenantList = React.useCallback(
    ( workspaces?: Workspace[] ): boolean => {
      if ( workspaces ) {
        const tenantName = workspaces.find( ( p ) => !!p.tenantName )?.tenantName || '';
        return !!workspaces.find( ( p ) => ( p.tenantName || '' ) !== tenantName );
      }

      return false;
    },
    [],
  );

  const getWorkspaceName = React.useCallback(
    ( workspace: Workspace ): string => {
      const suffix = workspace?.isDefault ? `(${t( 'profile.default' )})` : '';
      return `${workspace?.name} ${suffix}`;
    },
    [ t ],
  );

  const utils = React.useMemo<IWorkspaceHook>( () => {
    return {
      getWorkspaceName: ( workspace: Workspace ): string => {
        return getWorkspaceName( workspace );
      },
      sortByTenantName: ( workspaces?: Workspace[] ): Workspace[] => {
        return sortByTenantName( workspaces );
      },
      isMultiTenantList: ( workspaces?: Workspace[] ): boolean => {
        return isMultiTenantList( workspaces );
      },
      updateAccessToken: ( accessToken?: string ): void => {
        return updateAccessToken( accessToken );
      },
      updateAppAaccount: ( data?: TokenResponse ): void => {
        return updateAppAaccount( data );
      },
    };
  }, [ getWorkspaceName, isMultiTenantList, sortByTenantName, updateAccessToken, updateAppAaccount ] );

  return utils;
};
